import { defaultDataIdFromObject, useMutation } from '@apollo/client';
import { useIntl, defineMessages } from 'react-intl';
import * as immutable from 'object-path-immutable';

import CCEvents, { EventNames } from '@companycam/companycam-events';
import { graphQLIdDecoder } from 'helpers/graphql-utils';
import Steven from 'helpers/steven';

import TASK_LIST_SECTION_CREATE from 'components/projects/taskList/gql/TaskListSectionCreate.graphql';
import TASK_LIST_SECTION_UPDATE from 'components/projects/taskList/gql/taskListSectionUpdate.graphql';
import { TaskListSectionsFragment } from 'components/projects/taskList/gql/fragments';
import { getGqlErrors } from 'components/projects/taskList/helpers';
import toast from 'helpers/toast';

defineMessages({
  sectionCreateError: {
    id: 'Projects.taskList.taskListSection.sectionCreateError',
    defaultMessage: 'There was an error creating this section',
    description: 'message displayed when an error occurs creating section',
  },
  sectionEditError: {
    id: 'Projects.taskList.taskListSection.sectionEditError',
    defaultMessage: 'There was an error editing this section',
    description: 'message displayed when an error occurs editing section',
  },
});

const useTaskListSectionSave = ({ taskListId, onSuccess }) => {
  const { formatMessage } = useIntl();

  const showErrorToast = (errorMsg) => {
    toast.error(errorMsg);
  };

  const [taskListSectionCreateMutation] = useMutation(
    TASK_LIST_SECTION_CREATE,
    {
      update: (cache, { data }) => {
        const gqlErrors = getGqlErrors(data);

        if (!gqlErrors.length) {
          const cacheId = defaultDataIdFromObject({
            id: taskListId,
            __typename: 'TaskList',
          });
          const createdSection = data.taskListSectionCreate?.taskListSection;

          if (createdSection) {
            const cachedData = cache.readFragment({
              id: cacheId,
              fragment: TaskListSectionsFragment,
              fragmentName: 'TaskListSectionsFragment',
            });

            const updatedSectionTasksData = immutable.push(
              cachedData,
              `taskListSections.nodes`,
              createdSection,
            );

            cache.writeFragment({
              id: cacheId,
              fragment: TaskListSectionsFragment,
              fragmentName: 'TaskListSectionsFragment',
              data: updatedSectionTasksData,
            });
          }
        }
      },
      onCompleted: (data) => {
        const gqlErrors = getGqlErrors(data);
        const createdSection = data.taskListSectionCreate?.taskListSection;

        const newSectionId = createdSection?.id;

        if (!gqlErrors.length) {
          CCEvents.logEvent(EventNames.TASK_LISTS.CREATE_SECTION);
          onSuccess(createdSection.id);

          const sectionDatabaseId = graphQLIdDecoder(newSectionId)?.databaseId;

          Steven.entityCrud({
            entity_id: sectionDatabaseId,
            entity_type: 'checklist_section',
            action: 'created',
          });
        } else {
          showErrorToast(gqlErrors[0].message);
        }
      },
      onError: () => {
        showErrorToast(
          formatMessage({
            id: 'Projects.taskList.taskListSection.sectionCreateError',
          }),
        );
      },
    },
  );

  const [taskListSectionUpdateMutation] = useMutation(
    TASK_LIST_SECTION_UPDATE,
    {
      onError: () => {
        showErrorToast(
          formatMessage({
            id: 'Projects.taskList.taskListSection.sectionEditError',
          }),
        );
      },
    },
  );

  return {
    taskListSectionCreate: taskListSectionCreateMutation,
    taskListSectionUpdate: taskListSectionUpdateMutation,
  };
};

export default useTaskListSectionSave;
